// ثبت اعتبار و تخصیص اعتبارات (هزینه‌ای یا تملک)
// kind: 0 = هزینه‌ای (برنامه/فعالیت) ، 1 = تملک (طرح/پروژه)
// tab:  'budget' از تب بودجه (اعتبار و تخصیص) ، 'credit' از تب اعتبارات (تایید و ثبت دریافتی)
//       فیلدهای قفل هر حالت را سرور در پاسخ فهرست برمی‌گرداند و خودش هم اعمال می‌کند.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import ListPage, { ui } from '../../components/ListPage';
import JalaliDatePicker from '../../components/JalaliDatePicker';
import { useFormButtons } from '../../utils/vbPerms';
import { jsonOf, netErr } from '../../utils/http';

const toFa   = n => String(n ?? '').replace(/[0-9]/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
const toEng  = s => String(s ?? '').replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d).toString());
const fmtNum = n => n > 0 ? Number(n).toLocaleString('fa-IR') : '•';

const SCALES = ['ریال', 'هزار ریال', 'میلیون ریال'];
const EMPTY = {
  serial: '', letter_no: '', letter_date: '', save_date: '', remark: '',
  credit_id: 0, credit: '', program_id: 0, program: '', project_id: 0, project: '',
  hazineh_id: 0, hazineh: '', hazineh_id1: 0, hazineh1: '', hazineh_id2: 0, hazineh2: '',
  hazineh_id3: 0, hazineh3: '', hazineh_id4: 0, hazineh4: '', hazineh_id5: 0, hazineh5: '',
  credit_pay: '', credit_document: '', allocate_pay: '', allocate_document: '',
  receive_cash: '', receive_fund: '', back_receive_fund: '', receive_document: '',
  send_to_accounting: false,
};

const API = (path, opts = {}) => fetch(`/api/budget${path}`, {
  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('access')}` },
  ...opts,
}).then(jsonOf, netErr);

const Field = ({ label, value, onChange, type = 'text', flex = 1, disabled = false }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex }}>
    <label style={ui.mlabel}>{label}</label>
    <input type={type} value={value} onChange={onChange} disabled={disabled}
      style={{ ...ui.input, ...(disabled ? { background: '#f0f0f0', color: '#999', cursor: 'not-allowed' } : {}) }} />
  </div>
);

const TITLES = {
  budget: ['ثبت اعتبار و تخصیص اعتبارات هزینه ای', 'ثبت اعتبار و تخصیص اعتبارات تملک داراییهای سرمایه ای'],
  credit: ['تایید اعتبار و تخصیص و ثبت دریافتی اعتبارات هزینه ای',
           'تایید اعتبار و تخصیص و ثبت دریافتی اعتبارات تملک داراییهای سرمایه ای'],
};

function Lookup({ label, value, endpoint, kind, level, onPick, flex = 1 }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const search = async (text) => {
    setQ(text);
    const lv = level ? `&level=${level}` : '';
    const j = await API(`${endpoint}?kind=${kind}&q=${encodeURIComponent(text)}${lv}`);
    if (j.ok) { setRows(j.rows); setOpen(true); }
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex, position: 'relative' }}>
      <label style={ui.mlabel}>{label}</label>
      <input value={value || q} onFocus={() => search(q)} onChange={e => search(e.target.value)} placeholder="جستجو..." style={ui.input} />
      {value && <button onClick={() => { onPick({ id: 0, display: '' }); setQ(''); }}
        style={{ position: 'absolute', left: '6px', top: '30px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#c62828', fontSize: '14px' }}>✕</button>}
      {open && rows.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', right: 0, left: 0, background: '#fff', border: '1px solid #e0e0e0',
          borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)', zIndex: 700, maxHeight: '220px', overflowY: 'auto', marginTop: '2px' }}>
          {rows.map(r => (
            <div key={r.id} onClick={() => { onPick(r); setOpen(false); setQ(''); }}
              style={{ padding: '8px 10px', cursor: 'pointer', fontSize: '12px', borderBottom: '1px solid #f0f0f0' }}
              onMouseEnter={e => e.currentTarget.style.background = '#e8f0fe'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}>{r.display}</div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function BudgetTransaction({ user, onBack, kind = 0, tab = 'budget' }) {
  // دکمه‌های فرم FRMBUDGETTRANSACTION — دیدن = دسترسی
  const vbf = useFormButtons('FRMBUDGETTRANSACTION', ["BTNNEW","BTNEDIT","BTNDELETE","BTNOK"]);
  const title = (TITLES[tab] || TITLES.budget)[kind === 1 ? 1 : 0];
  const L = kind === 1
    ? { program: 'طرح', project: 'پروژه', title }
    : { program: 'برنامه', project: 'فعالیت', title };
  // فیلدهایی که در این تب قفل‌اند — از پاسخ سرور
  const [locked, setLocked] = useState([]);
  const lk = k => locked.includes(k);

  const curYear = new Date().toLocaleDateString('fa-IR').split('/')[0]
    .replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d).toString());

  const [year,   setYear]   = useState(curYear);
  const [search, setSearch] = useState('');
  const [scale,  setScale]  = useState(0);

  const [data,     setData]     = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [modal,    setModal]    = useState(null);
  const [form,     setForm]     = useState(EMPTY);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');
  const [toast,    setToast]    = useState('');

  const [total,    setTotal]    = useState(0);
  const [page,     setPage]     = useState(1);
  const [hasMore,  setHasMore]  = useState(false);
  const [loadMore, setLoadMore] = useState(false);

  const showToast = m => { setToast(m); setTimeout(() => setToast(''), 3000); };

  const cols = ['سریال', 'شماره نامه', 'تاریخ نامه', 'منبع اعتبار', L.program, L.project, 'کد هزینه',
    'اعتبار نقدی', 'اعتبار اسناد', 'تخصیص نقدی', 'تخصیص اسناد', 'دریافت نقدی', 'تنخواه دریافتی', 'اسناد دریافتی', 'توضیح'];
  const initW = [60, 90, 90, 150, 150, 150, 130, 100, 100, 100, 100, 100, 100, 100, 160];
  const [colW, setColW] = useState(initW);
  const colWRef = useRef(initW);
  useEffect(() => { colWRef.current = colW; }, [colW]);
  const startResize = useCallback((idx, e) => {
    e.preventDefault(); e.stopPropagation();
    const sx = e.clientX, sw = colWRef.current[idx];
    const onMove = ev => setColW(w => { const n = [...w]; n[idx] = Math.max(40, sw + (sx - ev.clientX)); return n; });
    const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    document.addEventListener('mousemove', onMove); document.addEventListener('mouseup', onUp);
  }, []);

  const buildUrl = (yr, pg) => `/transactions/?year=${yr}&kind=${kind}&tab=${tab}&search=${encodeURIComponent(search)}&page=${pg}`;

  const load = useCallback(async (yr = year) => {
    setLoading(true); setError(''); setPage(1); setSelected(null);
    const j = await API(buildUrl(yr, 1));
    setLoading(false);
    if (j.ok) { setData(j.data); setTotal(j.total || j.data.length); setHasMore(j.has_more || false); setLocked(j.locked || []); }
    else setError(j.msg);
  }, [year, search]);

  const loadNextPage = async () => {
    const next = page + 1; setLoadMore(true);
    const j = await API(buildUrl(year, next));
    setLoadMore(false);
    if (j.ok) { setData(d => [...d, ...j.data]); setHasMore(j.has_more || false); setPage(next); }
  };

  useEffect(() => { load(); }, []);

  const handleYear = e => {
    const v = toEng(e.target.value.replace(/[^0-9۰-۹]/g, '')).slice(0, 4);
    setYear(v);
    if (v.length === 4) load(v);
  };

  const F = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const openNew  = () => { if (!vbf.can('BTNNEW')) return; setForm({ ...EMPTY, save_date: '' }); setScale(0); setModal('new'); };
  const openEdit = () => { if (!vbf.can('BTNEDIT')) return;
    if (!selected) return;
    setForm({
      ...EMPTY, ...selected,
      credit_pay: selected.credit_pay || '', credit_document: selected.credit_document || '',
      allocate_pay: selected.allocate_pay || '', allocate_document: selected.allocate_document || '',
      receive_cash: selected.receive_cash || '', receive_fund: selected.receive_fund || '',
      back_receive_fund: selected.back_receive_fund || '', receive_document: selected.receive_document || '',
    });
    setScale(0);
    setModal('edit');
  };
  const openDel = () => { if (!vbf.can('BTNDELETE')) return; if (selected) setModal('delete'); };

  const save = async () => {
    if (!form.credit_id) { setError('منبع اعتبار انتخاب نشده است'); return; }
    setSaving(true); setError('');
    const body = JSON.stringify({ ...form, year, kind, scale, tab });
    const j = modal === 'new'
      ? await API('/transactions/create/', { method: 'POST', body })
      : await API(`/transactions/${selected.id}/update/`, { method: 'PUT', body });
    setSaving(false);
    if (j.ok) { setModal(null); setSelected(null); showToast(j.msg); load(); }
    else setError(j.msg);
  };

  const doDelete = async () => {
    setSaving(true);
    const j = await API(`/transactions/${selected.id}/delete/?tab=${tab}`, { method: 'DELETE' });
    setSaving(false);
    if (j.ok) { setModal(null); setSelected(null); showToast('حذف شد'); load(); }
    else { setError(j.msg); setModal(j.used ? 'delete_blocked' : null); }
  };

  const toolbar = (
    <>
      <div style={ui.toolBtns}>
        {vbf.can('BTNNEW') && <button onClick={openNew} style={ui.btnNew}>＋ جدید</button>}
        {vbf.can('BTNEDIT') && <button onClick={openEdit} disabled={!selected} style={{ ...ui.btnEdit, opacity: selected ? 1 : 0.4, cursor: selected ? 'pointer' : 'default' }}>✎ ویرایش</button>}
        {vbf.can('BTNDELETE') && <button onClick={openDel} disabled={!selected} style={{ ...ui.btnDel, opacity: selected ? 1 : 0.4, cursor: selected ? 'pointer' : 'default' }}>✕ حذف</button>}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
        <label style={ui.flabel}>سال مالی</label>
        <input value={toFa(year)} onChange={handleYear} maxLength={4} placeholder="۱۴۰۵" style={ui.yearInput} />
      </div>
      <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && load()}
        style={{ ...ui.searchInput, flex: 1, minWidth: '150px' }} placeholder="جستجو (سریال/نامه/توضیح)..." />
      {loading && <span>⏳</span>}
      {!loading && data.length > 0 && <div style={ui.countBadge}>{toFa(data.length)} از {toFa(total)} مورد</div>}
    </>
  );

  const footer = (hasMore && !search) ? (
    <button onClick={loadNextPage} disabled={loadMore} style={{
      padding: '9px 36px', background: loadMore ? '#ccc' : 'linear-gradient(135deg,#1a2f50,#1a237e)',
      color: '#fff', border: 'none', borderRadius: '8px', cursor: loadMore ? 'default' : 'pointer',
      fontFamily: 'inherit', fontSize: '13px', fontWeight: '700' }}>
      {loadMore ? '⏳ در حال بارگذاری...' : `▼ ۲۰۰ مورد بعدی (${toFa(String(total - data.length))} باقی‌مانده)`}
    </button>
  ) : null;

  return (
    <>
      <ListPage user={user} onBack={onBack} title={L.title} subtitle={tab === 'credit' ? 'بخش اعتبارات — سامانه ارجان' : 'بخش بودجه — سامانه ارجان'}
        toolbar={toolbar} error={!modal ? error : ''} footer={footer}>
        <table style={{ ...ui.table, tableLayout: 'fixed' }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
            <tr>
              {cols.map((h, i) => (
                <th key={i} style={{ ...ui.th, width: colW[i], minWidth: 40, position: 'relative', userSelect: 'none' }}>
                  {h}<span onMouseDown={e => startResize(i, e)} style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', cursor: 'col-resize' }} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((r, i) => {
              const sel = selected?.id === r.id;
              const bg  = sel ? '#1a2f50' : i % 2 === 0 ? '#fff' : '#fdf6e3';
              const tc  = sel ? '#fff' : '#111';
              const numCell = (v, color) => <td style={{ ...ui.tdNum, color: sel ? '#fff' : v > 0 ? color : '#ccc' }}>{fmtNum(v)}</td>;
              return (
                <tr key={r.id} style={{ background: bg, cursor: 'pointer' }}
                  onClick={() => setSelected(sel ? null : r)} onDoubleClick={() => { setSelected(r); setTimeout(openEdit, 0); }}
                  onMouseEnter={e => { if (!sel) e.currentTarget.style.background = '#e8f0fe'; }}
                  onMouseLeave={e => { if (!sel) e.currentTarget.style.background = bg; }}>
                  <td style={{ ...ui.td, fontWeight: '700', color: sel ? '#C8A84B' : '#1a237e' }}>{toFa(r.serial)}</td>
                  <td style={{ ...ui.td, color: tc }}>{r.letter_no || '•'}</td>
                  <td style={{ ...ui.td, fontFamily: 'monospace', fontSize: '11px', color: tc }}>{r.letter_date || '•'}</td>
                  <td style={{ ...ui.td, textAlign: 'right', fontSize: '11px', color: tc, overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.credit}>{r.credit || '•'}</td>
                  <td style={{ ...ui.td, textAlign: 'right', fontSize: '11px', color: tc, overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.program}>{r.program || '•'}</td>
                  <td style={{ ...ui.td, textAlign: 'right', fontSize: '11px', color: tc, overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.project}>{r.project || '•'}</td>
                  <td style={{ ...ui.td, textAlign: 'right', fontSize: '11px', color: tc, overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.hazineh}>{r.hazineh || '•'}</td>
                  {numCell(r.credit_pay, '#1b5e20')}
                  {numCell(r.credit_document, '#1b5e20')}
                  {numCell(r.allocate_pay, '#1a237e')}
                  {numCell(r.allocate_document, '#1a237e')}
                  {numCell(r.receive_cash, '#b71c1c')}
                  {numCell(r.receive_fund, '#e65100')}
                  {numCell(r.receive_document, '#6a1b9a')}
                  <td style={{ ...ui.td, textAlign: 'right', fontSize: '11px', color: tc, overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.remark}>{r.remark || '•'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!loading && data.length === 0 && <div style={ui.empty}>📭 داده‌ای یافت نشد</div>}
      </ListPage>

      {(modal === 'new' || modal === 'edit') && (
        <div style={ui.overlay} onClick={() => setModal(null)}>
          <div style={{ ...ui.modal, maxWidth: '820px' }} onClick={e => e.stopPropagation()}>
            <div style={ui.mHeader}>
              <div style={ui.mTitle}>{modal === 'new' ? '＋ ثبت اعتبار جدید' : `✎ ویرایش سریال ${toFa(selected?.serial)}`}</div>
              <button onClick={() => setModal(null)} style={ui.closeBtn}>✕</button>
            </div>
            <div style={ui.mBody}>
              {error && <div style={ui.errBox}>⚠️ {error}</div>}
              <div style={ui.row}>
                <Field label="شماره سریال (خالی = خودکار)" value={form.serial} onChange={e => F('serial', e.target.value)} type="number" flex={1} />
                <Field label="شماره نامه" value={form.letter_no} onChange={e => F('letter_no', e.target.value)} flex={1} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                  <label style={ui.mlabel}>تاریخ نامه</label>
                  <JalaliDatePicker value={form.letter_date} onChange={v => F('letter_date', v)} placeholder="تاریخ نامه" />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                  <label style={ui.mlabel}>تاریخ ثبت</label>
                  <JalaliDatePicker value={form.save_date} onChange={v => F('save_date', v)} placeholder="تاریخ ثبت" />
                </div>
              </div>
              <div style={ui.row}>
                <Lookup label="منبع اعتبار" value={form.credit} endpoint="/transactions/lookup/credit/" kind={kind}
                  onPick={r => setForm(f => ({ ...f, credit_id: r.id, credit: r.display }))} flex={2} />
              </div>
              <div style={ui.row}>
                <Lookup label={L.program} value={form.program} endpoint="/transactions/lookup/program/" kind={kind}
                  onPick={r => setForm(f => ({ ...f, program_id: r.id, program: r.display }))} />
                <Lookup label={L.project} value={form.project} endpoint="/transactions/lookup/project/" kind={kind}
                  onPick={r => setForm(f => ({ ...f, project_id: r.id, project: r.display }))} />
              </div>
              <div style={ui.divider} />
              <div style={{ fontSize: '12px', fontWeight: '700', color: '#1a237e', margin: '4px 0 8px' }}>کد هزینه (تا ۶ سطح)</div>
              <div style={ui.row}>
                <Lookup label="سطح ۱" value={form.hazineh}  endpoint="/transactions/lookup/hazineh/" kind={kind} onPick={r => setForm(f => ({ ...f, hazineh_id: r.id, hazineh: r.display }))} />
                <Lookup label="سطح ۲" value={form.hazineh1} endpoint="/transactions/lookup/hazineh/" kind={kind} onPick={r => setForm(f => ({ ...f, hazineh_id1: r.id, hazineh1: r.display }))} />
                <Lookup label="سطح ۳" value={form.hazineh2} endpoint="/transactions/lookup/hazineh/" kind={kind} onPick={r => setForm(f => ({ ...f, hazineh_id2: r.id, hazineh2: r.display }))} />
              </div>
              <div style={ui.row}>
                <Lookup label="سطح ۴" value={form.hazineh3} endpoint="/transactions/lookup/hazineh/" kind={kind} onPick={r => setForm(f => ({ ...f, hazineh_id3: r.id, hazineh3: r.display }))} />
                <Lookup label="سطح ۵" value={form.hazineh4} endpoint="/transactions/lookup/hazineh/" kind={kind} onPick={r => setForm(f => ({ ...f, hazineh_id4: r.id, hazineh4: r.display }))} />
                <Lookup label="سطح ۶" value={form.hazineh5} endpoint="/transactions/lookup/hazineh/" kind={kind} onPick={r => setForm(f => ({ ...f, hazineh_id5: r.id, hazineh5: r.display }))} />
              </div>
              <div style={ui.divider} />
              <div style={ui.row}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                  <label style={ui.mlabel}>مقیاس مبلغ</label>
                  <select value={scale} onChange={e => setScale(Number(e.target.value))} style={ui.input}>
                    {SCALES.map((o, i) => <option key={i} value={i}>{o}</option>)}
                  </select>
                </div>
                <div style={{ flex: 2 }} />
              </div>
              <div style={{ fontSize: '12px', fontWeight: '700', color: '#1a237e', margin: '4px 0 8px' }}>اعتبار</div>
              <div style={ui.row}>
                <Field label="اعتبار نقدی" value={form.credit_pay} onChange={e => F('credit_pay', e.target.value)} type="number" disabled={lk('credit_pay')} />
                <Field label="اعتبار اسناد خزانه" value={form.credit_document} onChange={e => F('credit_document', e.target.value)} type="number" disabled={lk('credit_document')} />
              </div>
              <div style={{ fontSize: '12px', fontWeight: '700', color: '#1a237e', margin: '4px 0 8px' }}>تخصیص</div>
              <div style={ui.row}>
                <Field label="تخصیص نقدی" value={form.allocate_pay} onChange={e => F('allocate_pay', e.target.value)} type="number" disabled={lk('allocate_pay')} />
                <Field label="تخصیص اسناد خزانه" value={form.allocate_document} onChange={e => F('allocate_document', e.target.value)} type="number" disabled={lk('allocate_document')} />
              </div>
              <div style={{ fontSize: '12px', fontWeight: '700', color: '#1a237e', margin: '4px 0 8px' }}>دریافت</div>
              <div style={ui.row}>
                <Field label="دریافت نقدی" value={form.receive_cash} onChange={e => F('receive_cash', e.target.value)} type="number" disabled={lk('receive_cash')} />
                <Field label="تنخواه دریافتی" value={form.receive_fund} onChange={e => F('receive_fund', e.target.value)} type="number" disabled={lk('receive_fund')} />
              </div>
              <div style={ui.row}>
                <Field label="تسویه تنخواه دریافتی" value={form.back_receive_fund} onChange={e => F('back_receive_fund', e.target.value)} type="number" />
                <Field label="اسناد دریافتی" value={form.receive_document} onChange={e => F('receive_document', e.target.value)} type="number" disabled={lk('receive_document')} />
              </div>
              <div style={ui.divider} />
              <div style={ui.row}>
                <Field label="توضیح" value={form.remark} onChange={e => F('remark', e.target.value)} />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', fontSize: '13px', color: '#444', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.send_to_accounting} disabled={lk('send_to_accounting')} onChange={e => F('send_to_accounting', e.target.checked)} style={{ marginLeft: '8px' }} />
                ارسال به حسابداری
              </label>
            </div>
            <div style={ui.mFooter}>
              <button onClick={() => { setModal(null); setError(''); }} style={ui.btnCancel}>انصراف</button>
              {vbf.can('BTNOK') && <button onClick={save} disabled={saving} style={ui.btnSave}>{saving ? '⏳ ...' : '💾 ذخیره'}</button>}
            </div>
          </div>
        </div>
      )}

      {modal === 'delete' && (
        <div style={ui.overlay} onClick={() => setModal(null)}>
          <div style={{ ...ui.modal, maxWidth: '440px' }} onClick={e => e.stopPropagation()}>
            <div style={{ ...ui.mHeader, background: 'linear-gradient(135deg,#b71c1c,#c62828)' }}>
              <div style={ui.mTitle}>⚠️ تأیید حذف</div>
              <button onClick={() => setModal(null)} style={ui.closeBtn}>✕</button>
            </div>
            <div style={{ ...ui.mBody, textAlign: 'center', padding: '28px' }}>
              <div style={{ fontSize: '36px', marginBottom: '12px' }}>🗑️</div>
              <div style={{ background: '#fff3f3', border: '1px solid #ffcdd2', borderRadius: '8px', padding: '10px 14px', fontWeight: '700', color: '#b71c1c' }}>
                سریال {toFa(selected?.serial)} — {selected?.credit}
              </div>
            </div>
            <div style={ui.mFooter}>
              <button onClick={() => setModal(null)} style={ui.btnCancel}>خیر</button>
              {vbf.can('BTNDELETE') && <button onClick={doDelete} disabled={saving} style={{ ...ui.btnSave, background: '#b71c1c' }}>{saving ? '⏳...' : 'بله، حذف'}</button>}
            </div>
          </div>
        </div>
      )}

      {modal === 'delete_blocked' && (
        <div style={ui.overlay} onClick={() => setModal(null)}>
          <div style={{ ...ui.modal, maxWidth: '420px' }} onClick={e => e.stopPropagation()}>
            <div style={{ ...ui.mHeader, background: 'linear-gradient(135deg,#e65100,#f57c00)' }}>
              <div style={ui.mTitle}>🚫 عدم امکان حذف</div>
              <button onClick={() => setModal(null)} style={ui.closeBtn}>✕</button>
            </div>
            <div style={{ ...ui.mBody, textAlign: 'center', padding: '26px', fontWeight: '700', color: '#1a2f50', lineHeight: '1.8' }}>{error}</div>
            <div style={ui.mFooter}><button onClick={() => { setModal(null); setError(''); }} style={ui.btnSave}>باشه</button></div>
          </div>
        </div>
      )}

      {toast && <div style={ui.toast}>{toast}</div>}
    </>
  );
}
